import * as React from 'react';
import { t, tryT, type MessageKey } from '@reos/i18n';
import { Card, Pill } from './CaseUiPrimitives.js';
import { captionOrPreparing } from '../lib/broker-text.js';
import { UnitCadastreForm } from './UnitCadastreForm.js';
import { CeeExtraOwnersCard } from './CeeExtraOwnersCard.js';
import {
  confirmHumanTask,
  listHumanTasks,
  skipHumanTask,
  uploadHumanTaskDocument,
  type HumanTask,
} from '../lib/human-tasks-api.js';

/**
 * Krok K0e — UKOLY PRO CLOVEKA v detailu zakazky.
 *
 * Pilot bezi rucne do prvnich plateb (rozhodnuti R.B. 24. 9. 2026), takze
 * vlastnika, omezeni, insolvenci a exekuce dohleda makler(ka) ve verejnych
 * registrech. Panel ukazuje, CO se ma udelat, KDE (odkaz z registru),
 * JAK (navod krok za krokem z i18n) a v jakem je to stavu.
 *
 * OSOBNI UDAJE (zadani K0e bod 5): panel NIKDY nezobrazuje jmeno vlastnika ani
 * rodne cislo - API je v ukolu nema. U lustrace osoby je proto popisek obecny
 * („vlastnik"), dokud clovek nenahraje podklad; jmeno pak zije jen ve faktech.
 *
 * ZADNY HARDCODED TEXT (zlate pravidlo 5): vsechno jde pres `dd.human.*`
 * a `webAdmin.humanTasks.*`, cs je zdroj a en ma paritu.
 */

const STATUS_STYLE: Readonly<Record<string, { bg: string; fg: string }>> = {
  PENDING: { bg: 'var(--rh-danger-bg)', fg: 'var(--rh-danger)' },
  UPLOADED: { bg: 'var(--rh-warning-bg)', fg: 'var(--rh-warning)' },
  EXTRACTED: { bg: 'var(--rh-accent-soft)', fg: 'var(--rh-accent-ink)' },
  CONFIRMED: { bg: 'var(--rh-success-bg)', fg: 'var(--rh-success)' },
  SKIPPED: { bg: 'var(--rh-rule)', fg: 'var(--rh-text-muted)' },
};

const cs = { locale: 'cs' } as const;

/**
 * `sourceId` chodi z API jako `string`, takze klic navodu nejde slozit
 * typove - stejny kompromis, jaky uz dela render reportu
 * (`technical-report-html.ts`). Klice hlida test parity katalogu.
 */
function tr(key: string, values?: Record<string, unknown>): string {
  return t(key as MessageKey, values === undefined ? cs : { ...cs, values });
}

function tryTr(key: string, values?: Record<string, unknown>): string | undefined {
  return tryT(key as MessageKey, values === undefined ? cs : { ...cs, values });
}

/**
 * Krok H1: ukol „doplni clovek" (`RESOLVE_UNKNOWN`) stoji na OTAZCE, ne na
 * zdroji - navod per zdroj (`dd.human.task.<id>.*`) pro nej neexistuje a `t()`
 * na chybejicim klici HAZI, takze bez tohohle rozliseni by jeden takovy ukol
 * shodil cely panel v detailu zakazky.
 */
function isResolveUnknown(task: HumanTask): boolean {
  return task.taskKind === 'RESOLVE_UNKNOWN';
}

/** Nazev ukolu: druh zdroje + predmet (parcela), nikdy jmeno osoby. */
function taskTitle(task: HumanTask): string {
  if (isResolveUnknown(task)) {
    const question = task.questionKeys[0] ?? task.subjectRef ?? '';
    // Krok UX1: klic otazky (`q-owner`) nikdy na obrazovku — viz lib/broker-text.
    return `${tr('dd.human.resolveUnknown.title')} — ${captionOrPreparing(
      tryTr(`dd.report.question.${question}`),
      question,
    )}`;
  }
  const title = tr(`dd.human.task.${task.sourceId}.title`);
  const subject = tr(`dd.human.task.${task.sourceId}.subject`, {
    subject: task.subjectRef ?? '',
  });
  return `${title} — ${subject}`;
}

/** Proc ukol existuje. U H1 je to duvod z reportu, u K0e navod zdroje. */
function taskWhy(task: HumanTask): string {
  if (isResolveUnknown(task)) {
    const reason =
      task.reasonCode === null
        ? undefined
        : tryTr(`dd.report.reason.${task.reasonCode}`, {
            named: 'NO',
            sources: '',
            detailed: 'NO',
            detail: '',
          });
    return reason ?? tr('dd.human.resolveUnknown.why');
  }
  return tr(`dd.human.task.${task.sourceId}.why`);
}

function costCaption(task: HumanTask): string {
  return task.costCzk === 0
    ? t('dd.human.cost.free', cs)
    : t('dd.human.cost.amount', { ...cs, values: { amount: String(task.costCzk) } });
}

/** Kroky navodu. Pocet nese registr (`human_task.steps`), texty i18n. */
function stepsOf(task: HumanTask, maxSteps: number): readonly string[] {
  // Krok H1: ukol nad otazkou navod per zdroj nema - odpovida se ve fronte.
  if (isResolveUnknown(task)) return [];
  const steps: string[] = [];
  for (let index = 1; index <= maxSteps; index += 1) {
    // `tryT`, ne `t`: navody maji ruzne dlouhy pocet kroku (4 az 5) a chybejici
    // klic tu NENI chyba katalogu, jen konec seznamu.
    const text = tryTr(`dd.human.task.${task.sourceId}.step.${String(index)}`, {
      subject: task.subjectRef ?? '',
    });
    if (text === undefined) break;
    steps.push(text);
  }
  return steps;
}

/** Nejvic kroku, kolik ma navod kteregokoli zdroje (registr: 4-5). */
const MAX_STEPS = 5;

/** URL v textu navodu ("Otevřete https://nahlizenidokn.cuzk.gov.cz/…"). */
const URL_RE = /https?:\/\/[^\s]+/g;

/**
 * Zivy pruchod 13. 10. (V11): kroky navodu „Co musím udělat" nesou adresu
 * registru (CÚZK, ARES…) jako holy text — makler ji musel rucne opsat.
 * Rozdeli text na kusy a URL promeni na klikaci odkaz; zbytek vety zustava
 * stejny.
 */
export function linkifyText(text: string): React.ReactNode {
  const matches = [...text.matchAll(URL_RE)];
  if (matches.length === 0) return text;
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  matches.forEach((match, index) => {
    const start = match.index ?? 0;
    const url = match[0];
    if (start > cursor) parts.push(text.slice(cursor, start));
    parts.push(
      <a key={`link-${String(index)}`} href={url} target="_blank" rel="noreferrer">
        {url}
      </a>,
    );
    cursor = start + url.length;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
}

function TaskRow({
  caseId,
  task,
  onChanged,
}: {
  caseId: string;
  task: HumanTask;
  onChanged: () => void;
}): React.JSX.Element {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const style = STATUS_STYLE[task.status] ?? STATUS_STYLE['PENDING'];

  const run = async (action: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await action();
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  const onPick = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file === undefined) return;
    void run(() => uploadHumanTaskDocument(caseId, task.id, file));
  };

  return (
    <div
      data-testid="human-task"
      data-source-id={task.sourceId}
      data-status={task.status}
      style={{ borderTop: '1px solid var(--rh-rule)', padding: '10px 0' }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <strong>{taskTitle(task)}</strong>
        <Pill caption={tr(`dd.human.status.${task.status}`)} bg={style?.bg ?? 'var(--rh-rule)'} fg={style?.fg ?? 'var(--rh-text-muted)'} />
        <Pill caption={costCaption(task)} bg="var(--rh-canvas)" fg="var(--rh-text-muted)" />
      </div>

      <p style={{ margin: '4px 0', color: 'var(--rh-text-muted)', fontSize: 'var(--rh-size-meta)' }}>
        {taskWhy(task)}
      </p>

      <ol style={{ margin: '4px 0 8px 18px', fontSize: 'var(--rh-size-meta)', color: 'var(--rh-text-muted)' }}>
        {stepsOf(task, MAX_STEPS).map((step, index) => (
          <li key={`${task.id}-step-${String(index)}`}>{linkifyText(step)}</li>
        ))}
      </ol>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {task.url !== '' && (
          <a href={task.url} target="_blank" rel="noreferrer noopener" className="underline" data-testid="human-task-link">
            {t('webAdmin.humanTasks.open', cs)}
          </a>
        )}

        {/*
          Krok H1: odpoved se zadava ve FRONTE „K doplneni" (jeden formular pro
          vsechny zakazky), ne dvakrat. Panel zakazky proto jen ukazuje, ze se
          na otazce pracuje, a vede tam.
        */}
        {isResolveUnknown(task) && (
          <a href="/k-doplneni" className="underline" data-testid="human-task-queue-link">
            {t('webAdmin.resolveUnknown.openQueue', cs)}
          </a>
        )}

        {!isResolveUnknown(task) && task.status !== 'CONFIRMED' && task.status !== 'SKIPPED' && (
          <label style={{ fontSize: 'var(--rh-size-meta)' }}>
            {t('webAdmin.humanTasks.upload', cs)}{' '}
            <input
              type="file"
              accept="image/png,application/pdf"
              disabled={busy}
              onChange={onPick}
              data-testid="human-task-upload"
            />
          </label>
        )}

        {!isResolveUnknown(task) && task.status === 'EXTRACTED' && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void run(() => confirmHumanTask(caseId, task.id))}
            data-testid="human-task-confirm"
          >
            {t('webAdmin.humanTasks.confirm', cs)}
          </button>
        )}

        {!isResolveUnknown(task) && task.status === 'PENDING' && task.costCzk > 0 && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void run(() => skipHumanTask(caseId, task.id))}
            data-testid="human-task-skip"
          >
            {t('webAdmin.humanTasks.skip', cs)}
          </button>
        )}
      </div>

      {task.documents.length > 0 && (
        <p style={{ margin: '6px 0 0', fontSize: 'var(--rh-size-meta)', color: 'var(--rh-text-muted)' }} data-testid="human-task-documents">
          {t('webAdmin.humanTasks.documentCount', {
            ...cs,
            values: { count: String(task.documents.length) },
          })}
        </p>
      )}

      {error !== null && (
        <p role="alert" style={{ margin: '6px 0 0', fontSize: 'var(--rh-size-meta)', color: 'var(--rh-danger)' }}>
          {error}
        </p>
      )}
    </div>
  );
}

export function HumanTasksPanel({
  caseId,
  caseKind,
}: {
  caseId: string;
  /**
   * Krok B1c bod 9: u BYTU (`FLAT`) je jediny rucni vstup vysledek Nahlizeni
   * do KN na jednotku. Formular se proto ukazuje i tehdy, kdyz zakazka zadny
   * `dd_human_tasks` radek nema — u bytu je otazka otevrena jako `HUMAN_TASK`
   * v osnove, ne jako ukol s podkladem.
   */
  caseKind?: string | undefined;
}): React.JSX.Element | null {
  const [tasks, setTasks] = React.useState<readonly HumanTask[] | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const reload = React.useCallback((): void => {
    listHumanTasks(caseId).then(
      (items) => {
        setTasks(items);
        setLoadError(null);
      },
      (cause: unknown) => {
        setLoadError(cause instanceof Error ? cause.message : String(cause));
      },
    );
  }, [caseId]);

  React.useEffect(reload, [reload]);

  const showUnitCadastre = caseKind === 'FLAT';

  // Zakazka bez lidskych ukolu panel vubec nezobrazuje - prazdna karta by jen
  // zabirala misto v zalozce, kde uz otazky jsou. U BYTU to neplati: formular
  // pro jednotku je jediny rucni vstup celeho behu (B1c bod 9).
  if (loadError === null && (tasks === null || tasks.length === 0)) {
    return (
      <>
        <CeeExtraOwnersCard caseId={caseId} />
        {showUnitCadastre ? <UnitCadastreForm caseId={caseId} onSaved={reload} /> : null}
      </>
    );
  }

  return (
    <>
    <CeeExtraOwnersCard caseId={caseId} />
    <Card
      heading={t('webAdmin.humanTasks.heading', cs)}
      hint={t('webAdmin.humanTasks.hint', cs)}
      testId="human-tasks"
    >
      {loadError !== null ? (
        <p role="alert" style={{ margin: 0, color: 'var(--rh-danger)' }}>
          {loadError}
        </p>
      ) : (
        (tasks ?? []).map((task) => (
          <TaskRow key={task.id} caseId={caseId} task={task} onChanged={reload} />
        ))
      )}
    </Card>
    {showUnitCadastre && <UnitCadastreForm caseId={caseId} onSaved={reload} />}
    </>
  );
}
